import {
  type BigDecimal,
  createBigDecimal,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { gateAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/spot/my_trades',
    query: {
      limit: config.limit.toString(),
      ...(from ? { from: Math.floor(from / 1000).toString() } : {}), // Convert to seconds
      ...(to ? { to: Math.ceil(to / 1000).toString() } : {}), // Convert to seconds
    },
    responseSchema,
  })

  // Sort by create_time_ms in descending order
  const sortedTrades = response.sort(
    (a, b) => Number(b.create_time_ms) - Number(a.create_time_ms),
  )

  // Data processing
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const actions: Action[] = []

  // Group trades by orderId and create_time
  const groupedTrades = new Map<string, typeof sortedTrades>()

  for (const item of sortedTrades) {
    const timestamp = Math.floor(Number(item.create_time_ms))

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    const key = `${item.order_id}_${item.create_time}`
    const group = groupedTrades.get(key) || []
    group.push(item)
    groupedTrades.set(key, group)
  }

  // Process grouped trades
  for (const trades of groupedTrades.values()) {
    if (trades.length === 0) continue

    const firstTrade = trades[0]
    const [base, quote] = firstTrade.currency_pair.split('_')

    // Get assets
    const baseAsset = getAsset(base)
    const quoteAsset = getAsset(quote)

    if (!baseAsset) {
      throw new AssetNotFoundException(base)
    }
    if (!quoteAsset) {
      throw new AssetNotFoundException(quote)
    }

    // Calculate total amounts
    let totalBaseAmount = createBigDecimal(0n)
    let totalQuoteAmount = createBigDecimal(0n)
    for (const trade of trades) {
      totalBaseAmount = plus(totalBaseAmount, trade.amount)
      totalQuoteAmount = plus(
        totalQuoteAmount,
        times(trade.amount, trade.price),
      )
    }

    // Create transfers array
    const transfers: Action['transfers'] = [
      {
        asset: baseAsset,
        amount: totalBaseAmount,
        direction: firstTrade.side === 'buy' ? 'in' : 'out',
      },
      {
        asset: quoteAsset,
        amount: totalQuoteAmount,
        direction: firstTrade.side === 'buy' ? 'out' : 'in',
      },
    ]

    // Group fees by currency
    const feeGroups = new Map<string, BigDecimal>()

    for (const trade of trades) {
      if (!trade.fee || !trade.fee_currency) {
        continue
      }
      const existingFee = feeGroups.get(trade.fee_currency)
      if (existingFee) {
        feeGroups.set(trade.fee_currency, plus(existingFee, trade.fee))
      } else {
        feeGroups.set(trade.fee_currency, trade.fee)
      }
    }

    // Add fee transfers
    for (const [feeCurrency, totalFee] of feeGroups.entries()) {
      const feeAsset = getAsset(feeCurrency)
      if (!feeAsset) {
        throw new AssetNotFoundException(feeCurrency)
      }
      if (totalFee.value === 0n) {
        continue // Skip zero fees
      }

      transfers.push({
        asset: feeAsset,
        amount: totalFee,
        direction: 'out',
      })
    }

    // Create action
    actions.push({
      comment: `Trade ID: ${trades.map((t) => t.id).join(' / ')}`,
      evidence: 'system-rule',
      order: 0,
      source: `${gateAccountCategory}_${config.type}_${firstTrade.order_id}_${firstTrade.create_time}`,
      type: 'trade',
      timestamp: Math.floor(Number(firstTrade.create_time_ms)),
      transfers,
    })
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources: [],
    stopped: false,
    to: fetchedTo,
  }
}
